<template>
    <div>
        <DTable
            id="prices-table"
            :fields="fields"
            :items="tableItems"
            class="border prices-table table-responsive"
        >
            <template #action-header>
                <DButton
                    id="price-btn-add"
                    class="float-end"
                    color="primary"
                    icon="plus"
                    :mini="true"
                    :disabled="rowAddDisabled"
                    @click="showAddPriceModal"
                />
            </template>
            <template #start_date="{item}">
                <td v-if="item.start_date">
                    {{ item.start_date }}
                </td>
                <td
                    v-else
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
            </template>
            <template #price="{item}">
                <td v-if="item.price">
                    {{ priceFormatForDisplay(item.price, 2, customer.market_region_id) }}
                </td>
                <td
                    v-else
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
            </template>
            <template #third_party_cost="{item}">
                <td v-if="item.third_party_cost">
                    {{ priceFormatForDisplay(item.third_party_cost, 2, customer.market_region_id) }}
                </td>
                <td
                    v-else
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
            </template>
            <template #billing_cycle="{item}">
                <td v-if="item.billing_cycle || isSelectedPlanOneTime">
                    {{ isSelectedPlanOneTime === false ? billingCycle(item.billing_cycle) : '' }}
                </td>
                <td
                    v-else
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
            </template>
            <template #discount="{item}">
                <td v-if="discountEmpty(item)" />
                <td
                    v-else-if="discountInvalid(item)"
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
                <td v-else>
                    {{ `${item.discount_type === 'percentage' ? percentageFormatForDisplay(item.discount_value) : priceFormatForDisplay(item.discount_value, 2, customer.market_region_id) } ${item.discount_name}` }}
                </td>
            </template>
            <template #setup_fee="{item}">
                <td v-if="item.setup_fee">
                    {{ priceFormatForDisplay(item.setup_fee, 2, customer.market_region_id) }}
                </td>
                <td
                    v-else
                    class="invalid-value"
                >
                    {{ __('customers.contracts.invalid') }}
                </td>
            </template>
            <template #phase_text="{item}">
                <td v-if="item.phase_text">
                    {{ item.phase_text }}
                </td>
                <td
                    v-else
                >
                    {{ '' }}
                </td>
            </template>
            <template #action="{item}">
                <td class="text-right">
                    <DButton
                        v-if="rowEditDisabled === false"
                        :id="`price-btn-edit-${item.index}`"
                        icon="pencil"
                        :mini="true"
                        @click="showEditPriceModal(item.index)"
                    />
                    <DButton
                        :id="`price-btn-delete-${item.index}`"
                        color="danger"
                        icon="trash-can-outline"
                        :mini="true"
                        :disabled="rowDeletionDisabled"
                        @click="removePrice(item.index)"
                    />
                </td>
            </template>
        </DTable>
        <PriceModal
            v-if="priceModalVisible"
            :show-modal="priceModalVisible"
            :errors-data="modalErrors"
            :price="modalPrice"
            :billing-cycles="billingCycles"
            :discount-types="discountTypes"
            :is-selected-plan-one-time="isSelectedPlanOneTime"
            :disable-start-date-change="disableStartDateChange"
            :index="priceIndex"
            @save-price="savePrice"
            @close-modal="closeModal"
        />
    </div>
</template>

<script>
import PriceModal from '~@/Components/Customers/Contracts/Modals/PriceModal.vue'
import billingCycle from '~@/Mixins/billingCycle'
import priceFormat from '~@/Mixins/priceFormat'

const getNewPriceBase = () => {
    return {
        start_date: null,
        price: null,
        third_party_cost: null,
        billing_cycle: null,
        discount_name: null,
        discount_value: null,
        discount_type: null,
        setup_fee: null
    }
}

export default {
    name: 'Prices',
    components: { PriceModal },
    mixins: [
        billingCycle,
        priceFormat
    ],
    props: {
        contract: {
            type: Object,
            default: () => { return {} }
        },
        form: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        billingCycles: {
            type: Array,
            default: () => { return [] }
        },
        discountTypes: {
            type: Array,
            default: () => { return [] }
        },
        isSelectedPlanOneTime: {
            type: Boolean,
            default: false
        },
        isPreLiveContract: {
            type: Boolean,
            default: false
        },
        customer: {
            type: Object,
            default: () => { return {} }
        }
    },
    emits: ['updatePrice', 'addPrice', 'removePrice'],
    data () {
        return {
            fields: [
                { key: 'start_date', label: this.__('customers.contracts.start_date'), _style: { width: '14%' } },
                { key: 'price', label: this.__('customers.contracts.price'), _style: { width: '14%' } },
                { key: 'third_party_cost', label: this.__('customers.contracts.third_party_cost'), _style: { width: '14%' } },
                { key: 'billing_cycle', label: this.__('customers.contracts.billing_cycle'), _style: { width: '14%' } },
                { key: 'discount', label: this.__('customers.contracts.discount'), _style: { width: '14%' } },
                { key: 'setup_fee', label: this.__('customers.contracts.setup_fee'), _style: { width: '14%' } },
                { key: 'phase_text', label: this.__('customers.contracts.phase_text'), _style: { width: '14%' } },
                { key: 'action', label: '', _classes: 'text-right', _style: { width: '16%' } }
            ],
            priceModalVisible: false,
            priceIndex: null,
            modalPrice: {},
            modalErrors: {},
            disableStartDateChange: false,
            hideErrors: false
        }
    },
    computed: {
        tableItems () {
            return this.items.map((item, index) => ({ index, ...item }))
        },
        rowAddDisabled () {
            return this.isPreLiveContract === false || this.isSelectedPlanOneTime === true
        },
        rowEditDisabled () {
            return this.isPreLiveContract === false
        },
        rowDeletionDisabled () {
            return this.isPreLiveContract === false || this.isSelectedPlanOneTime || Object.values(this.form.prices).length === 1
        },
        items () {
            const items = []

            for (const priceItemIndex in this.form.prices) {
                if (this.rowHasErrors(this.errors, priceItemIndex) && this.hideErrors === false) {
                    items.push({ ...this.form.prices[priceItemIndex], _classes: 'row-has-errors' })
                } else {
                    items.push(this.form.prices[priceItemIndex])
                }
            }

            return items
        }
    },
    watch: {
        errors () {
            this.hideErrors = false
        }
    },
    methods: {
        showAddPriceModal () {
            this.modalPrice = getNewPriceBase()
            this.priceIndex = null

            this.setDisableStartDateChange()
            this.setModalErrors()

            this.priceModalVisible = true
        },
        showEditPriceModal (index) {
            if (this.rowEditDisabled) {
                return
            }

            this.modalPrice = this.form.prices[index]
            this.priceIndex = index

            this.setDisableStartDateChange()
            this.setModalErrors()

            this.priceModalVisible = true
        },
        setDisableStartDateChange () {
            this.disableStartDateChange = false

            if (this.priceIndex === 0) {
                this.modalPrice.start_date = this.form.start_date
                this.disableStartDateChange = true
            }
        },
        setModalErrors () {
            if (this.priceIndex === null) {
                this.modalErrors = {}

                return
            }

            this.modalErrors = this.getErrorsForRow(this.errors, this.priceIndex)
        },
        rowHasErrors (errors, index) {
            return Object.values(this.getErrorsForRow(errors, index)).length > 0
        },
        getErrorsForRow (errors, index) {
            const rowErrors = {}

            if (typeof errors[`prices.${index}.start_date`] !== 'undefined') {
                rowErrors.start_date = errors[`prices.${index}.start_date`]
            }

            if (typeof errors[`prices.${index}.billing_cycle`] !== 'undefined') {
                rowErrors.billing_cycle = errors[`prices.${index}.billing_cycle`]
            }

            if (typeof errors[`prices.${index}.price`] !== 'undefined') {
                rowErrors.price = errors[`prices.${index}.price`]
            }

            if (typeof errors[`prices.${index}.discount_name`] !== 'undefined') {
                rowErrors.discount_name = errors[`prices.${index}.discount_name`]
            }

            if (typeof errors[`prices.${index}.discount_value`] !== 'undefined') {
                rowErrors.discount_value = errors[`prices.${index}.discount_value`]
            }

            if (typeof errors[`prices.${index}.discount_type`] !== 'undefined') {
                rowErrors.discount_type = errors[`prices.${index}.discount_type`]
            }

            if (typeof errors[`prices.${index}.third_party_cost`] !== 'undefined') {
                rowErrors.third_party_cost = errors[`prices.${index}.third_party_cost`]
            }

            if (typeof errors[`prices.${index}.setup_fee`] !== 'undefined') {
                rowErrors.setup_fee = errors[`prices.${index}.setup_fee`]
            }

            return rowErrors
        },
        discountEmpty (item) {
            // Discount is empty if nothing is set
            const sum = (item.discount_name ? 1 : 0) + (item.discount_value ? 1 : 0) + (item.discount_type ? 1 : 0)

            return sum === 0
        },
        discountInvalid (item) {
            // Discount is valid if all 3 are set or none
            const sum = (item.discount_name ? 1 : 0) + (item.discount_value ? 1 : 0) + (item.discount_type ? 1 : 0)

            return sum !== 0 && sum !== 3
        },
        savePrice (priceData) {
            this.priceIndex !== null
                ? this.$emit('updatePrice', this.priceIndex, priceData)
                : this.$emit('addPrice', priceData)
        },
        removePrice (index) {
            this.hideErrors = true

            this.$emit('removePrice', index)
        },
        closeModal () {
            this.priceModalVisible = false
        }
    }
}
</script>

<style lang="scss">
.prices-table {
    .invalid-value {
        color: var(--colorDanger);
        font-weight: bold;
    }
    td {
        vertical-align: middle;
    }
    table {
        margin-bottom: 0 !important;
    }
}
</style>
